import { ArrowLeftOutlined, CompressOutlined, EditFilled, SyncOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Button, Col, Flex, Input, Row } from 'antd';
import { useGenerateMessageMutation, useRegenerateWithInstructionsMessageMutation } from 'api/MessageAiApi';
import { MessageTypeEnum } from 'api/MessagesApi';
import AIProgressBar from 'components/Message/CreateMessageComponent/StepMessage/AiCreation/AIProgressBar/AIProgressBar';
import { useMessageService } from 'components/Message/useMessageService';
import SpinComponent from 'components/Tool/SpinComponent/SpinComponent';
import { useCallback, useEffect, useState } from 'react';
import './AiCreationGenerating.css';

type AllProps = {
  initTitle?: string;
  initContent?: string;
  tones: string[];
  emotionFocus?: string;
  memory?: string;
  closing?: string;
  otherInfo?: string;
  onBack: () => void;
  onSave: (text: string, title: string) => void;
};

function AiCreationGenerating({
  initTitle,
  initContent,
  tones,
  emotionFocus,
  memory,
  closing,
  otherInfo,
  onBack,
  onSave
}: AllProps) {
  const { message } = useMessageService();
  const [draftTitle, setDraftTitle] = useState(initTitle || '');
  const [draftText, setDraftText] = useState(initContent || '');
  const [showCustomEdit, setShowCustomEdit] = useState(false);
  const [customInstruction, setCustomInstruction] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const [generateMessage, { isLoading: generateLoading, error: generateError }] = useGenerateMessageMutation();
  const [regenerateWithInstruction, { isLoading: regenerateLoading, error: regenerateError }] =
    useRegenerateWithInstructionsMessageMutation();

  const handleSubmit = async () => {
    onSave(draftText, draftTitle);
  };

  const handleCustomEdit = () => {
    return handleRegenerateWithInstructions(customInstruction);
  };

  const handleMakeShorter = () => {
    return handleRegenerateWithInstructions('Make this message shorter and more concise while keeping the key points.');
  };
  const handleMakeSimpler = () => {
    return handleRegenerateWithInstructions(
      'Make this message simpler and easier to understand, using more straightforward language.'
    );
  };

  const handleGenerate = useCallback(async () => {
    setLocalError(null);
    try {
      const { title, content } = await generateMessage({
        tones,
        emotionFocus,
        memory,
        closing,
        otherInfo,
        type: message?.type || MessageTypeEnum.Text,
        eventType: message?.eventType || '',
        recipients: (message?.contacts || []).map((contact) => ({
          name: contact.name,
          relationship: contact.relationship
        }))
      }).unwrap();

      setDraftText(content);
      setDraftTitle(title);
    } catch (e) {
      console.error(e);
      setLocalError('Failed to generate message. Please try again.');
    }
  }, [closing, emotionFocus, generateMessage, memory, message?.contacts, message?.eventType, message?.type, otherInfo, tones]);

  const handleRegenerateWithInstructions = async (instructions: string) => {
    setLocalError(null); // Clear previous errors
    try {
      const { title, content } = await regenerateWithInstruction({
        tones,
        emotionFocus,
        memory,
        closing,
        otherInfo,
        type: message?.type || MessageTypeEnum.Text,
        eventType: message?.eventType || '',
        recipients: (message?.contacts || []).map((contact) => ({
          name: contact.name,
          relationship: contact.relationship
        })),
        draftText,
        instructions
      }).unwrap();

      setDraftText(content);
      setDraftTitle(title);
    } catch (e) {
      console.error(e);
      setLocalError('Failed to regenerate message. Please try again.');
    }
  };

  useEffect(() => {
    if (!initContent) {
      void handleGenerate();
    }
  }, [handleGenerate, initContent]);

  const isLoading = generateLoading || regenerateLoading;

  if (!message) return null;

  return (
    <div className="ai-creation-generating create-message-component-full-height">
      <div>
        <AIProgressBar currentStep={5} />
        <div className="create-message-component-title">Here’s a first draft based on what you shared</div>
        <div className="create-message-component-subtitle">
          Review and edit your message, or regenerate it with different parameters.
        </div>

        {(generateError || regenerateError || localError) && (
          <Alert
            message="AI Generation Failed"
            description={localError || 'An error occurred during message generation. Please try again.'}
            type="error"
            style={{ marginBottom: '16px' }}
            closable
            onClose={() => setLocalError(null)}
          />
        )}

        <SpinComponent isLoading={isLoading} title="Loading message data...">
          <div>
            <Input.TextArea
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              rows={12}
              placeholder="Your AI-generated message will appear here..."
            />
          </div>
        </SpinComponent>

        {showCustomEdit && (
          <div className="custom-edit-container">
            <label>Custom Edit Instructions:</label>
            <Input.TextArea
              value={customInstruction}
              onChange={(e) => setCustomInstruction(e.target.value)}
              placeholder="Describe how you'd like to modify the message..."
              rows={3}
            />
            <div className="custom-edit-buttons">
              <Button onClick={handleCustomEdit} type="primary" disabled={!customInstruction || regenerateLoading}>
                Apply Edit
              </Button>
              <Button onClick={() => setShowCustomEdit(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {draftText && (
          <div className="buttons-container">
            <Button
              icon={<CompressOutlined />}
              onClick={handleMakeShorter}
              className="action-button"
              disabled={isLoading}
            >
              Make shorter
            </Button>

            <Button icon={<SyncOutlined />} onClick={handleGenerate} className="action-button" disabled={isLoading}>
              Regenerate
            </Button>

            <Button icon={<UserOutlined />} onClick={handleMakeSimpler} className="action-button" disabled={isLoading}>
              Make simpler
            </Button>

            <Button icon={<EditFilled />} onClick={() => setShowCustomEdit(!showCustomEdit)} className="action-button">
              Custom edit
            </Button>
          </div>
        )}
      </div>

      <Row className="create-message-component-footer">
        <Col span={12}>
          <Button onClick={onBack} type="link">
            <ArrowLeftOutlined /> Back
          </Button>
        </Col>
        <Col span={12} className="text-right">
          <Flex gap={20} justify="flex-end">
            <Button type="primary" onClick={() => handleSubmit()} disabled={!draftText || generateLoading}>
              Next
            </Button>
          </Flex>
        </Col>
      </Row>
    </div>
  );
}

export default AiCreationGenerating;
